import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import classNames from 'classnames';

import { msg } from 'api';
import { gtm } from 'apps/gtm';

import { getProductDetailsLink } from 'apps/products/links';

import { IMAGE_SIZES, getProductImageUrl } from 'utils/productImages';

import ProductWarnings from 'apps/products/models/ProductWarnings';
import { priceFormat } from 'apps/products/utils/priceFormat';

import Locations from 'apps/basket/models/Locations';

import { dynamicYieldService } from 'apps/dynamicYield';

const LOCATION = { pathname: 'trolley' };
const PARAMS = {};
// for webshop BOP compliance only; TODO remove after Raven BOP is enabled

export default class MiniTrolleyItem extends PureComponent {
  static propTypes = {
    analyticsContextBasketItems: PropTypes.any,
    basketItem: PropTypes.shape({
      isUpdating: PropTypes.bool,
      quantity: PropTypes.number,
    }).isRequired,
    betaFeatures: PropTypes.array.isRequired,
    changeBasketQuantity: PropTypes.func.isRequired,
    product: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
    productWarnings: PropTypes.arrayOf(PropTypes.oneOf(Object.values(ProductWarnings))),
    shouldTrimProductName: PropTypes.bool,
    shouldShowTotalPrice: PropTypes.bool,
  };

  static defaultProps = {
    productWarnings: [],
    shouldTrimProductName: true,
    shouldShowTotalPrice: false,
  };

  static triggerTrolleyEvent = (product, quantityDiff, analyticsContextBasketItems) => {
    const productData = {
      gtmPageType: 'Trolley sidebar', // TODO this should be specific to page e.g. 'Offers'
      quantity: Math.abs(quantityDiff),
      section: 'Trolley sidebar',
      product,
    };

    dynamicYieldService.reportCartEvent({
      product, quantityDelta: quantityDiff, basketItems: analyticsContextBasketItems,
    });
    dynamicYieldService.reportSyncCartEvent({ basketItems: analyticsContextBasketItems });
    gtm.trigger(quantityDiff > 0 ? 'gtm:addToTrolley' : 'gtm:removeFromTrolley', productData);
  };

  submitQuantityUpdate = (quantityDiff) => {
    const { product, basketItem, analyticsContextBasketItems } = this.props;
    const { sku } = basketItem;

    const analyticsDataSubset = {
      product,
      section: {
        attributes: { type: 'MINI_TROLLEY' },
        id: 'MINI_TROLLEY',
      },
    };

    this.props.changeBasketQuantity({
      items: { [sku]: quantityDiff },
      location: Locations.MINI_TROLLEY,
      analyticsContextData: analyticsDataSubset,
    });
    MiniTrolleyItem.triggerTrolleyEvent(product, quantityDiff, analyticsContextBasketItems);
  };

  getProductName = () => {
    const { product, shouldTrimProductName } = this.props;

    if (!shouldTrimProductName) {
      return product.name;
    }

    let shortProductName = product.name.slice(0, 25);
    if (shortProductName.length !== product.name.length) {
      shortProductName += '\u2026';
    }

    return shortProductName;
  }

  getTotalPrice = () => {
    const { basketItem, shouldShowTotalPrice } = this.props;

    if (!shouldShowTotalPrice) {
      return false;
    }

    return (
      <div className={classNames(
        'hd-miniTrolley__totalPrice', { 'hd-miniTrolley__totalPrice--isUpdating': basketItem.isUpdating })}
      >
        {priceFormat({ value: basketItem.totalPrice })}
      </div>
    );
  }

  increase = () => {
    this.submitQuantityUpdate(1);
  };

  decrease = () => {
    this.submitQuantityUpdate(-1);
  };

  delete = () => {
    const { quantity } = this.props.basketItem;
    this.submitQuantityUpdate(-quantity);
  };

  hasProductWarning = () => this.props.productWarnings.length > 0;

  render() {
    const { product, basketItem, betaFeatures } = this.props;
    const { isUpdating, quantity } = basketItem;

    if (!product || !quantity) {
      return null; // product not yet fetched
    }

    return (
      <li className="hd-miniTrolley__item">
        <b className="hd-miniTrolley__defaultQuantity">{quantity}</b>

        <Link
          to={getProductDetailsLink({ product, location: LOCATION, params: PARAMS, betaFeatures })}
          className="hd-miniTrolley__imgContainer"
        >
          {/* WE USE IMAGE SIZE FROM FOP TO NOT DOWNLOAD NEW FILE */}
          <img
            alt={product.name}
            className="hd-miniTrolley__img"
            src={getProductImageUrl(basketItem.sku, product.image, IMAGE_SIZES.S_150)}
          />
        </Link>
        <Link
          to={getProductDetailsLink({ product, location: LOCATION, params: PARAMS, betaFeatures })}
          className="hd-miniTrolley__name"
          title={product.name}
        >
          {this.getProductName()}
        </Link>
        { this.getTotalPrice() }
        <div className="hd-miniTrolley__controls">
          <button
            className="hd-miniTrolley__delete"
            onClick={this.delete}
            aria-label="Delete from trolley"
          />
          <button
            className="hd-miniTrolley__decrease"
            onClick={this.decrease}
            disabled={isUpdating}
            aria-label="Remove 1 from trolley"
          >
            {msg.miniTrolley.decreaseBtnLabel}
          </button>
          <strong className="hd-miniTrolley__quantity">{quantity}</strong>
          <button
            className="hd-miniTrolley__increase"
            disabled={this.hasProductWarning() || isUpdating}
            onClick={this.increase}
            aria-label="Add 1 to trolley"
          >
            {msg.miniTrolley.increaseBtnLabel}
          </button>
        </div>
      </li>
    );
  }
}
